import { FC, useMemo } from 'react';

import * as ui from '@april9au/stack9-ui';
import { Form } from 'antd';
import dayjs from 'dayjs';

const S9Select = ui.Select.default;
const { useFormInstance, useWatch } = ui.S9Form;

interface AppointmentSlotSelectProps {
  eventId: number | undefined;
  disabled?: boolean;
}

export const AppointmentSlotSelect: FC<AppointmentSlotSelectProps> = ({
  eventId,
  disabled,
}) => {
  const form = useFormInstance();
  const slotId = useWatch(['slot_id'], form);

  const { data: slotsResponse, isValidating } = ui.useScreenQuery<{
    results: Array<{ id: number; start_dt: string; is_available?: boolean }>;
  }>('getappointmentslotsbyeventid', {
    vars: {
      id: eventId,
      page: 0,
      limit: 100,
      filters: JSON.stringify({}),
    },
    shouldFetch: !!eventId,
    filters: {
      is_available: { operation: 'eq', value: true },
    } as never,
  });

  const { data: selectedSlotData } = ui.useScreenQueryById<{
    id: number;
    start_dt: string;
  }>('getappointmentslotbyid', slotId, {
    shouldFetch:
      !!slotId && !slotsResponse?.results?.find(s => s.id === slotId),
  });

  const options = useMemo(() => {
    const availableOptions =
      slotsResponse?.results?.map(slot => ({
        label: dayjs(slot.start_dt).format('DD/MM/YYYY HH:mm'),
        value: slot.id,
      })) || [];

    if (
      selectedSlotData &&
      slotId &&
      !availableOptions.find(opt => opt.value === slotId)
    ) {
      return [
        {
          label: dayjs(selectedSlotData.start_dt).format('DD/MM/YYYY HH:mm'),
          value: selectedSlotData.id,
        },
        ...availableOptions,
      ];
    }

    return availableOptions;
  }, [slotsResponse, selectedSlotData, slotId]);

  return (
    <Form.Item
      label="Appointment Slot"
      name="slot_id"
      rules={[
        {
          required: true,
          message: 'Appointment slot is required for appointment bookings',
        },
      ]}
    >
      <S9Select
        style={{ width: '100%' }}
        showSearch
        options={options || []}
        loading={isValidating}
        disabled={disabled || !eventId}
        placeholder={
          !eventId ? 'Select an event first' : 'Select appointment slot'
        }
      />
    </Form.Item>
  );
};
